/** Brand colours and the small text formatters the website cards share. */

export const NAVY = '#1c2b39';
export const TEAL = '#299c8f';

/**
 * MP dates are wall clock in the church's zone with no offset
 * ("2026-09-20T08:00:00"). Build a local Date from the parts so the browser
 * never shifts them; the site's visitors are in that zone.
 */
export function parseMp(value: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value);
  if (!m) return new Date(value);
  return new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] ?? 0),
    Number(m[5] ?? 0),
    Number(m[6] ?? 0)
  );
}

/** "2026-09" for grouping and the month filter. */
export function monthKey(value: string): string {
  return value.slice(0, 7);
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "September" or "September 2027" when the year is not the current one. */
export function monthLabel(key: string, now: Date = new Date()): string {
  const [y, m] = key.split('-').map(Number);
  const name = MONTHS[(m ?? 1) - 1] ?? key;
  return y === now.getFullYear() ? name : `${name} ${y}`;
}

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Sun, Sep 20" (adds the year when it is not this year). */
export function fmtDay(d: Date, now: Date = new Date()): string {
  const base = `${DAY[d.getDay()]}, ${MON[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base}, ${d.getFullYear()}`;
}

/** "8:00 AM", "12:30 PM" */
export function fmtTime(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return m === 0 ? `${h} ${ampm}` : `${h}:${String(m).padStart(2, '0')} ${ampm}`;
}

/** "9:30 AM" from an MP time-of-day column ("09:30:00"). */
export function fmtClock(value: string): string {
  const m = /^(\d{2}):(\d{2})/.exec(value);
  if (!m) return value;
  return fmtTime(new Date(2000, 0, 1, Number(m[1]), Number(m[2])));
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * One date's line: "Sun, Sep 20 · 8 AM – 1 PM", or for a multi-day event
 * "Fri, Oct 3 – Sun, Oct 5".
 */
export function fmtWhen(start: string, end: string | null): { day: string; time: string } {
  const s = parseMp(start);
  const e = end ? parseMp(end) : null;
  if (e && !sameDay(s, e)) {
    return { day: `${fmtDay(s)} – ${fmtDay(e)}`, time: fmtTime(s) };
  }
  const time = e && e.getTime() !== s.getTime() ? `${fmtTime(s)} – ${fmtTime(e)}` : fmtTime(s);
  return { day: fmtDay(s), time };
}

/** "$25", "$12.50": cents only when there are cents. */
export function fmtMoney(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
}

/** "Free", "$25", "$12.50" */
export function fmtPrice(basePrice: number | null, hasProduct: boolean): string | null {
  if (!hasProduct) return null;
  if (basePrice === null || basePrice <= 0) return 'Free';
  return fmtMoney(basePrice);
}

/** "Troy Campus" -> "Troy" for chips where the word Campus is implied. */
export function campusShort(name: string | null): string {
  return (name ?? '').replace(/\s+campus$/i, '').trim();
}

/** "Troy Campus", or "Church wide" for the shared congregation. */
export function campusLabel(congregationId: number, congregationName: string): string {
  if (congregationId === 1) return 'Church wide';
  return `${campusShort(congregationName)} Campus`;
}

export function shortDay(day: string): string {
  return day.slice(0, 3);
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const head = parts[0] ?? '';
  if (parts.length === 1) return head.slice(0, 2).toUpperCase();
  const tail = parts[parts.length - 1] ?? '';
  return ((head[0] ?? '') + (tail[0] ?? '')).toUpperCase();
}

/** "Brian Tolly" / "Brian Tolly and Jane Dabbs" / "Brian, Jane and 2 more" */
export function leaderLine(leaders: string[]): string {
  if (leaders.length === 0) return '';
  if (leaders.length === 1) return leaders[0] ?? '';
  if (leaders.length === 2) return `${leaders[0]} and ${leaders[1]}`;
  return `${leaders[0]}, ${leaders[1]} and ${leaders.length - 2} more`;
}

export function pluralEvents(n: number): string {
  return n === 1 ? '1 event' : `${n} events`;
}

export function pluralDates(n: number): string {
  return n === 1 ? '1 date' : `${n} dates`;
}

export function pluralGroups(n: number): string {
  return n === 1 ? '1 group' : `${n} groups`;
}

export function pluralOpportunities(n: number): string {
  return n === 1 ? '1 serve opportunity' : `${n} serve opportunities`;
}
